/**
 * AlignmentIntelligencePage — Interactive Guided Reflection Layer
 *
 * Post-certificate experience that turns the static Page 2 PDF into a living
 * conversation tool. Partners explore each category's shared themes, tensions,
 * and tier-toned discussion prompts.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronRight,
  MessageCircle,
  AlertCircle,
  ArrowLeft,
  BadgeCheck,
  Loader2,
  Download,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tierThemeMap = {
  CORE: {
    accent: 'text-[#2F9ED8]',
    border: 'border-[#2F9ED8]/35',
    glow: 'shadow-[0_0_30px_rgba(47,158,216,0.12)]',
    softBg: 'bg-[#2F9ED8]/10',
    gradient: 'from-[#2F9ED8]/20 to-transparent',
    ringHex: 'rgba(47,158,216,0.45)',
  },
  RITUAL: {
    accent: 'text-[#5AB2FF]',
    border: 'border-[#5AB2FF]/35',
    glow: 'shadow-[0_0_30px_rgba(90,178,255,0.12)]',
    softBg: 'bg-[#5AB2FF]/10',
    gradient: 'from-[#5AB2FF]/25 to-transparent',
    ringHex: 'rgba(90,178,255,0.45)',
  },
  LEGACY: {
    accent: 'text-[#C8A75E]',
    border: 'border-[#C8A75E]/40',
    glow: 'shadow-[0_0_30px_rgba(200,167,94,0.14)]',
    softBg: 'bg-[#C8A75E]/10',
    gradient: 'from-[#C8A75E]/25 to-transparent',
    ringHex: 'rgba(200,167,94,0.45)',
  },
};

const labelToneMap = {
  'Exceptional Alignment': 'bg-white text-black',
  'Strong Alignment': 'bg-white/90 text-black',
  'Good Alignment': 'bg-white/70 text-black',
  'Moderate Alignment': 'bg-amber-100 text-amber-950',
  'Limited Alignment': 'bg-orange-100 text-orange-950',
  'Significant Misalignment': 'bg-red-100 text-red-950',
};

const cx = (...xs) => xs.filter(Boolean).join(' ');

const getScoreDescriptor = (s) => {
  if (s >= 90) return 'Exceptionally aligned';
  if (s >= 75) return 'Strongly aligned';
  if (s >= 60) return 'Well aligned';
  if (s >= 45) return 'Moderately aligned';
  if (s >= 25) return 'Lightly aligned';
  return 'Needs deeper conversation';
};

function ScoreBadge({ score, label, size = 'sm' }) {
  const isLg = size === 'lg';
  return (
    <div
      className={cx(
        'flex items-center',
        isLg ? 'gap-3.5' : 'gap-3'
      )}
      data-testid="score-badge"
    >
      <div
        className={cx(
          'flex items-center justify-center rounded-2xl border border-white/10 bg-white/5 font-semibold text-white',
          isLg ? 'h-14 w-14 text-xl sm:h-16 sm:w-16 sm:text-2xl' : 'h-12 w-12 text-lg'
        )}
      >
        {score}
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 sm:text-xs">Alignment Score</p>
        <Badge
          className={cx(
            'mt-1 border-0 whitespace-normal break-words',
            labelToneMap[label] || 'bg-white text-black'
          )}
        >
          {label}
        </Badge>
      </div>
    </div>
  );
}

function OverallPanel({ data, theme }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={cx(
        'relative overflow-hidden rounded-[24px] border bg-[#0C0D10] p-5 sm:rounded-[28px] sm:p-7 md:p-10',
        theme.border,
        theme.glow
      )}
      data-testid="overall-panel"
    >
      <div className={cx('pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b', theme.gradient)} />
      <div className="relative">
        {/*
          Mobile: stacked vertical rhythm — title → headline → names → score badge.
          Desktop: original two-column layout with score floated right.
        */}
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between md:gap-8">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.24em] text-white/40 sm:text-xs sm:tracking-[0.28em]">
              Marrsing Alignment Intelligence
            </p>
            <h1
              className="mt-3 text-[28px] font-semibold leading-[1.18] text-white sm:text-[32px] md:text-5xl md:leading-tight"
              style={{ textWrap: 'balance', wordBreak: 'normal', overflowWrap: 'break-word', hyphens: 'manual' }}
            >
              Your shared reflection
            </h1>
            <p className="mt-3 text-sm leading-6 text-white/55 md:mt-2 md:text-base">
              {data.partner_a_name} <span className="text-white/30">&amp;</span> {data.partner_b_name}
              <span className="ml-2 text-white/30">·</span>
              <span className="ml-2 text-white/55">{data.tier} tier</span>
            </p>
          </div>
          <div className="md:shrink-0">
            <ScoreBadge
              score={data.overall_alignment_score}
              label={data.overall_alignment_label}
              size="lg"
            />
          </div>
        </div>

        <p
          className="mt-7 max-w-[60ch] text-[16px] leading-[1.65] text-white/80 md:mt-8 md:max-w-3xl md:text-base md:leading-8"
        >
          {data.overall_summary}
        </p>

        <div className="mt-7 grid gap-3.5 sm:gap-4 md:mt-8 md:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-[10px] uppercase tracking-[0.22em] text-white/45 sm:text-xs">
              Top shared values
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {(data.top_shared_values || []).length === 0 && (
                <span className="text-sm text-white/50">Still emerging</span>
              )}
              {(data.top_shared_values || []).map((v) => (
                <Badge
                  key={v}
                  variant="secondary"
                  className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/85"
                  data-testid={`shared-value-${v.replace(/\s+/g, '-')}`}
                >
                  {v}
                </Badge>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-amber-300/15 bg-amber-300/5 p-5">
            <p className="text-[10px] uppercase tracking-[0.22em] text-amber-100/75 sm:text-xs">
              Growth edges
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {(data.growth_edges || []).length === 0 && (
                <span className="text-sm text-amber-50/70">
                  No major tensions surfaced
                </span>
              )}
              {(data.growth_edges || []).map((g) => (
                <Badge
                  key={g}
                  className="rounded-full border border-amber-300/25 bg-amber-300/10 px-3 py-1 text-xs text-amber-50/90"
                  data-testid={`growth-edge-${g.replace(/\s+/g, '-')}`}
                >
                  {g}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function CategoryCard({ item, theme, onOpen, index }) {
  const hasTension = item.tension_points && item.tension_points.length > 0;
  return (
    <motion.button
      layout
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.05 * index }}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.995 }}
      onClick={onOpen}
      data-testid={`category-card-${item.category.replace(/\s+/g, '-')}`}
      className={cx(
        'group relative w-full overflow-hidden rounded-[24px] border bg-[#0F1014] p-0 text-left transition-all',
        theme.border,
        theme.glow,
        'hover:border-white/30'
      )}
    >
      <div
        className={cx('absolute inset-x-0 top-0 h-24 bg-gradient-to-b', theme.gradient)}
      />
      <Card className="border-0 bg-transparent shadow-none">
        <CardHeader className="space-y-5 p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-white/40">
                Category
              </p>
              <h3 className="mt-2 text-xl text-white">{item.category}</h3>
            </div>
            <ChevronRight className="mt-1 h-5 w-5 text-white/35 transition-transform group-hover:translate-x-1" />
          </div>

          <ScoreBadge score={item.alignment_score} label={item.alignment_label} />

          <p
            className="line-clamp-3 text-[15px] leading-[1.6] text-white/80 sm:text-sm sm:leading-6 sm:text-white/75"
          >
            {item.summary}
          </p>
        </CardHeader>

        <CardContent className="space-y-4 px-6 pb-6">
          <div>
            <div className="mb-2 flex items-center justify-between text-xs text-white/45">
              <span>{getScoreDescriptor(item.alignment_score)}</span>
              <span>{item.alignment_score}%</span>
            </div>
            <Progress value={item.alignment_score} className="h-2 bg-white/10" />
          </div>

          <div className="flex flex-wrap gap-2">
            {(item.shared_themes || []).slice(0, 3).map((t) => (
              <Badge
                key={t}
                variant="secondary"
                className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/80"
              >
                {t}
              </Badge>
            ))}
            {(item.shared_themes || []).length === 0 && (
              <span className="text-xs text-white/40">
                No shared themes surfaced yet
              </span>
            )}
          </div>

          {hasTension ? (
            <div className="rounded-2xl border border-amber-300/15 bg-amber-300/5 p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 text-amber-200" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-amber-100/70">
                    Tension to explore
                  </p>
                  <p className="mt-1 text-sm leading-6 text-amber-50/90">
                    {item.tension_points[0]}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-[var(--mc-blue-mist)] bg-[var(--mc-blue-fog)] p-4">
              <div className="flex items-start gap-2">
                <BadgeCheck className="mt-0.5 h-4 w-4 text-mc-blue-light" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-mc-blue-light/70">
                    Alignment signal
                  </p>
                  <p className="mt-1 text-sm leading-6 text-mc-blue-pale/90">
                    Clear harmony here — no major tension surfaced.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div
            className={cx(
              'flex items-center justify-between rounded-2xl border p-4',
              theme.border,
              theme.softBg
            )}
          >
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/45">
                Explore together
              </p>
              <p className="mt-1 text-sm text-white/80">
                Open discussion prompts
              </p>
            </div>
            <div className={cx('rounded-full p-2', theme.softBg)}>
              <MessageCircle className={cx('h-4 w-4', theme.accent)} />
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.button>
  );
}

function CategoryDrawer({ item, theme, onClose, onDownload }) {
  if (!item) return null;
  const hasTension = item.tension_points && item.tension_points.length > 0;
  return (
    <AnimatePresence>
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="category-drawer-backdrop"
      >
        <motion.div
          key="panel"
          initial={{ opacity: 0, y: 40, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          transition={{ duration: 0.22 }}
          onClick={(e) => e.stopPropagation()}
          className="absolute inset-x-2 bottom-2 top-12 mx-auto max-w-4xl overflow-hidden rounded-[24px] border border-white/10 bg-[#0C0D10] shadow-2xl sm:inset-x-3 sm:bottom-3 sm:top-16 sm:rounded-[28px]"
          data-testid="category-drawer"
        >
          <div className={cx('h-1.5 w-full bg-gradient-to-r', theme.gradient)} />
          <div className="h-full overflow-y-auto p-5 sm:p-7 md:p-10">
            <div className="flex items-start justify-between gap-3 sm:gap-4">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.28em] text-white/40">
                  Alignment Intelligence
                </p>
                <h2
                  className="mt-2 text-[26px] leading-[1.18] text-white sm:text-3xl md:text-4xl md:leading-tight"
                  style={{ textWrap: 'balance', wordBreak: 'normal', overflowWrap: 'break-word' }}
                >
                  {item.category}
                </h2>
                <p
                  className="mt-3 max-w-[60ch] text-[15px] leading-[1.65] text-white/65 md:max-w-2xl md:text-base md:leading-7"
                >
                  A closer look at how this category is shaping your connection,
                  where you already align, and what invites deeper conversation.
                </p>
              </div>
              <Button
                variant="ghost"
                onClick={onClose}
                className="rounded-full text-white/70 hover:bg-white/10 hover:text-white"
                data-testid="close-drawer-btn"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-3">
              <div className="rounded-[24px] border border-white/10 bg-white/[0.03] p-5">
                <ScoreBadge
                  score={item.alignment_score}
                  label={item.alignment_label}
                />
                <div className="mt-5">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-white/45">
                    Interpretation
                  </p>
                  <p className="mt-2 text-sm text-white/80">
                    {getScoreDescriptor(item.alignment_score)}
                  </p>
                </div>
                <Progress
                  value={item.alignment_score}
                  className="mt-6 h-2 bg-white/10"
                />
              </div>

              <div className="rounded-[24px] border border-white/10 bg-white/[0.03] p-5 md:col-span-2">
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/45">
                  Shared reflection
                </p>
                <p className="mt-3 text-[16px] leading-[1.65] text-white/85 sm:text-[15px] sm:leading-7">
                  {item.summary}
                </p>

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-white/45">
                      Partner A priorities
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(item.partner_a_priorities || []).map((p) => (
                        <span
                          key={p}
                          className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-white/80"
                        >
                          {p}
                        </span>
                      ))}
                      {(item.partner_a_priorities || []).length === 0 && (
                        <span className="text-xs text-white/40">—</span>
                      )}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-white/45">
                      Partner B priorities
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(item.partner_b_priorities || []).map((p) => (
                        <span
                          key={p}
                          className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-white/80"
                        >
                          {p}
                        </span>
                      ))}
                      {(item.partner_b_priorities || []).length === 0 && (
                        <span className="text-xs text-white/40">—</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {hasTension && (
              <div className="mt-6 rounded-2xl border border-amber-300/15 bg-amber-300/5 p-5">
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 text-amber-200" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-amber-100/75">
                      Tensions to explore
                    </p>
                    <ul className="mt-2 space-y-1.5 text-sm leading-6 text-amber-50/90">
                      {item.tension_points.map((t) => (
                        <li key={t}>• {t}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8">
              <p className="text-[10px] uppercase tracking-[0.28em] text-white/40">
                Discussion prompts
              </p>
              <div className="mt-3 grid gap-3">
                {(item.discussion_prompts || []).map((p, i) => (
                  <motion.div
                    key={p}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.35, delay: 0.06 * i }}
                    className={cx(
                      'flex items-start gap-3 rounded-2xl border p-4 transition-colors',
                      theme.border,
                      theme.softBg
                    )}
                    data-testid={`discussion-prompt-${i}`}
                  >
                    <div
                      className={cx(
                        'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border',
                        theme.border
                      )}
                    >
                      <span className={cx('text-xs font-semibold', theme.accent)}>
                        {i + 1}
                      </span>
                    </div>
                    <p className="text-[16px] leading-[1.65] text-white/85 sm:text-[15px] sm:leading-7">{p}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <Button
                variant="outline"
                onClick={onDownload}
                className="rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10"
                data-testid="download-certificate-drawer-btn"
              >
                <Download className="mr-2 h-4 w-4" />
                Download certificate PDF
              </Button>
              <Button
                onClick={onClose}
                className="rounded-full bg-white text-black hover:bg-white/90"
                data-testid="close-drawer-primary-btn"
              >
                Close
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function AlignmentIntelligencePage() {
  const navigate = useNavigate();
  const { relationshipId: paramId } = useParams();
  const [searchParams] = useSearchParams();

  const relationshipId = useMemo(() => {
    return (
      paramId ||
      searchParams.get('assessment_id') ||
      sessionStorage.getItem('relationship_id')
    );
  }, [paramId, searchParams]);

  const token = searchParams.get('token') || sessionStorage.getItem('reveal_token') || '';

  const [data, setData] = useState(null);
  const [savedSteps, setSavedSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!relationshipId) {
        setError('No relationship ID. Complete your assessment first.');
        setLoading(false);
        return;
      }
      try {
        const r = await axios.get(
          `${API}/v2/assessments/${relationshipId}/alignment-intelligence`,
          { params: token ? { token } : {} }
        );
        if (mounted) {
          setData(r.data);
          setLoading(false);
        }
        // Best-effort load of saved guided-conversation steps
        if (token) {
          try {
            const s = await axios.get(
              `${API}/v2/relationships/${relationshipId}/guided-conversation/steps`,
              { params: { token } }
            );
            if (mounted) setSavedSteps(s.data?.steps || []);
          } catch { /* non-blocking */ }
        }
      } catch (e) {
        if (!mounted) return;
        const msg =
          e.response?.data?.detail ||
          'Unable to load your Alignment Intelligence. Please try again.';
        setError(msg);
        setLoading(false);
        toast.error(msg);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [relationshipId]);

  const theme = useMemo(
    () => tierThemeMap[data?.tier] || tierThemeMap.CORE,
    [data?.tier]
  );

  const handleDownload = () => {
    if (!relationshipId) return;
    window.open(`${API}/certificates/${relationshipId}/download`, '_blank');
  };

  if (loading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[#0A0B0E] text-white"
        data-testid="alignment-loading"
      >
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-white/60" />
          <p className="text-sm text-white/50">
            Preparing your Alignment Intelligence…
          </p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[#0A0B0E] p-6 text-white"
        data-testid="alignment-error"
      >
        <div className="max-w-md rounded-2xl border border-white/10 bg-white/5 p-6 text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-amber-300" />
          <p className="mt-3 text-base text-white/85">
            {error || 'Something went wrong.'}
          </p>
          <Button
            onClick={() => navigate('/certificate')}
            className="mt-5 rounded-full bg-white text-black hover:bg-white/90"
            data-testid="error-back-btn"
          >
            Back to certificate
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#0A0B0E] text-white"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
      data-testid="alignment-intelligence-page"
    >
      <div className="mx-auto max-w-6xl px-5 py-7 sm:px-6 sm:py-8 md:py-14">
        <div className="mb-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/certificate')}
            className="self-start rounded-full text-white/70 hover:bg-white/10 hover:text-white"
            data-testid="back-to-certificate-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to certificate
          </Button>
          <Button
            variant="outline"
            onClick={handleDownload}
            className="self-end rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10 sm:self-auto"
            data-testid="download-pdf-btn"
          >
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </Button>
        </div>

        <OverallPanel data={data} theme={theme} />

        <div className="mt-10 md:mt-14">
          <div className="mb-6 flex flex-col gap-1.5 sm:mb-5 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.28em] text-white/40">
                Categories
              </p>
              <h2
                className="mt-2 text-[26px] font-semibold leading-[1.2] text-white sm:text-2xl md:text-3xl"
                style={{ textWrap: 'balance' }}
              >
                Explore your alignment
              </h2>
            </div>
            <span className="text-xs text-white/45">
              Tap a card to open prompts
            </span>
          </div>

          <div
            className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="category-grid"
          >
            {(data.category_results || []).map((c, i) => (
              <CategoryCard
                key={c.category}
                item={c}
                theme={theme}
                index={i}
                onOpen={() => setActive(c)}
              />
            ))}
          </div>
        </div>

        {savedSteps.length > 0 && (
          <div
            className="mt-12 rounded-[24px] border border-white/10 bg-white/[0.03] p-5 sm:p-6 md:p-8"
            data-testid="ai-saved-agreements"
          >
            <p className="text-[10px] uppercase tracking-[0.28em] text-white/40">
              Saved Agreements
            </p>
            <h3
              className="mt-2 text-[24px] leading-[1.2] text-white sm:text-2xl"
              style={{ textWrap: 'balance' }}
            >
              What you have chosen to carry forward
            </h3>
            <p className="mt-3 max-w-[60ch] text-[16px] leading-[1.65] text-white/60 sm:max-w-2xl sm:text-sm sm:leading-7">
              These are the agreements and reflections you saved during your guided conversations. They are now part of your Alignment Intelligence™.
            </p>
            <div className="mt-6 grid gap-3 md:grid-cols-2">
              {savedSteps.map((s) => (
                <div
                  key={s.saved_step_id}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-5"
                  data-testid={`ai-saved-step-${s.saved_step_id}`}
                >
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#C8A75E]">
                    {s.category || 'Reflection'}
                  </p>
                  {s.agreement ? (
                    <p className="mt-2 text-base leading-7 text-white">
                      “{s.agreement}”
                    </p>
                  ) : null}
                  {s.insight ? (
                    <p className="mt-2 text-sm leading-6 text-white/70">
                      {s.insight}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 rounded-[24px] border border-white/10 bg-white/[0.03] p-5 sm:p-6 md:mt-14 md:p-8">
          <p className="text-[10px] uppercase tracking-[0.28em] text-white/40">
            Continue the conversation
          </p>
          <h3
            className="mt-2 text-[24px] leading-[1.2] text-white sm:text-2xl"
            style={{ textWrap: 'balance' }}
          >
            This is a living conversation, not a verdict
          </h3>
          <p className="mt-3 max-w-[60ch] text-[16px] leading-[1.65] text-white/65 sm:max-w-2xl sm:text-sm sm:leading-7">
            Use these prompts over the next few weeks. Revisit any category
            together whenever clarity feels needed. Your alignment evolves as
            you do.
          </p>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button
              onClick={handleDownload}
              className="rounded-full bg-white text-black hover:bg-white/90"
              data-testid="cta-download-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              Download certificate PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/upgrade')}
              className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/10"
              data-testid="cta-upgrade-btn"
            >
              Explore advanced features
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <CategoryDrawer
        item={active}
        theme={theme}
        onClose={() => setActive(null)}
        onDownload={handleDownload}
      />
    </div>
  );
}
